import { LogLevel } from '@grafana/faro-react';
import { captureLog } from '../../../web-tracing';

export type ContentSegment = {
	type:
		| 'text'
		| 'chart'
		| 'artifact'
		| 'maxiterations'
		| 'proposals-group'
		| 'integration-credentials'
		| 'oauth-required';
	content: string;
	// Additional metadata for oauth-required type
	integrationId?: string;
};

interface RunSqlResult {
	status: 'success' | 'error';
	error: string | null;
	result: Array<Record<string, string | number | null>> | null;
}

interface ExecuteSqlEntry {
	query: string;
	result: RunSqlResult | string;
}

// Pattern to detect OAuth errors and extract integration ID if present
const OAUTH_ERROR_PATTERN = /has not connected via OAuth/i;
const INTEGRATION_ID_PATTERN =
	/integration[_\s]?id[:\s]*["']?([a-f0-9-]{36})["']?/i;

function isRunSqlResult(value: unknown): value is RunSqlResult {
	return typeof (value as RunSqlResult)?.status === 'string';
}

function isExecuteSqlOutput(value: unknown): value is ExecuteSqlEntry[] {
	return (
		Array.isArray(value) &&
		value.every((item: any) => item?.query && item?.result)
	);
}

function safeJsonParse(value: string): RunSqlResult | ExecuteSqlEntry[] | null {
	try {
		const parsed: unknown = JSON.parse(value);
		if (isExecuteSqlOutput(parsed)) return parsed;
		if (isRunSqlResult(parsed)) return parsed;
		return null;
	} catch (e: unknown) {
		if (!(e instanceof SyntaxError)) {
			captureLog(
				['Unexpected error parsing tool output as JSON', String(e), value],
				LogLevel.WARN
			);
		}
		return null;
	}
}

function hasExecuteSqlError(entries: ExecuteSqlEntry[]): boolean {
	return entries.some((entry) => {
		// If result is a string, it's an error message from run_sql
		if (typeof entry.result === 'string') {
			return true;
		}
		return entry.result.status === 'error';
	});
}

/**
 * Determines if a tool output indicates an actual error.
 *
 * For SQL tools (execute_sql, run_sql) that return JSON with RunSqlResult objects,
 * we parse the JSON and check the `status` field instead of doing naive string matching.
 * This avoids false positives where the JSON key `"error": null` in a successful response
 * would be incorrectly detected as an error.
 */
export function hasToolOutputError(
	toolOutput: string | undefined,
	toolName: string
): boolean {
	if (!toolOutput) return false;

	if (toolName === 'execute_sql' || toolName === 'run_sql') {
		const parsed = safeJsonParse(toolOutput);

		if (parsed !== null) {
			if (Array.isArray(parsed)) {
				return hasExecuteSqlError(parsed);
			}
			return parsed.status === 'error';
		}
	}

	// Generic string-based check for non-SQL tools or non-JSON outputs
	return (
		toolOutput.toLowerCase().includes('error') ||
		toolOutput.toLowerCase().includes('failed') ||
		toolOutput.toLowerCase().includes('exception')
	);
}

/**
 * Check if content contains an OAuth authentication error
 */
export function detectOAuthError(content: string): {
	hasOAuthError: boolean;
	integrationId?: string;
} {
	const hasOAuthError = OAUTH_ERROR_PATTERN.test(content);
	if (!hasOAuthError) {
		return { hasOAuthError: false };
	}

	// Try to extract integration ID from the content
	const integrationIdMatch = content.match(INTEGRATION_ID_PATTERN);
	return {
		hasOAuthError: true,
		integrationId: integrationIdMatch?.[1],
	};
}

/**
 * Process content to find and extract chart and artifact tags
 * Returns an array of content segments and their IDs
 */
export function processContentWithEmbedded(
	content: string
): Array<ContentSegment> {
	if (!content) return [{ type: 'text', content: '' }];

	const chartTagRegex = /<secoda-chart>([^<]+)<\/secoda-chart>/g;
	const artifactTagRegex = /<secoda-artifact>([^<]+)<\/secoda-artifact>/g;
	const maxIterationsTagRegex =
		/<secoda-maxiterations>([^<]+)<\/secoda-maxiterations>/g;
	const proposalsTagRegex =
		/<secoda-proposals-group>([^<]+)<\/secoda-proposals-group>/g;
	const integrationCredentialsTagRegex =
		/<secoda-integration-credentials>([^<]+)<\/secoda-integration-credentials>/g;
	const oauthRequiredTagRegex =
		/<secoda-oauth-required>([^<]+)<\/secoda-oauth-required>/g;

	const segments: Array<ContentSegment> = [];

	let lastIndex = 0;
	let match;

	// Find all matching tags and extract them
	const processRegex = (regex: RegExp, type: ContentSegment['type']) => {
		regex.lastIndex = 0; // Reset regex state
		while ((match = regex.exec(content)) !== null) {
			// Add text before the tag
			if (match.index > lastIndex) {
				segments.push({
					type: 'text',
					content: content.substring(lastIndex, match.index),
				});
			}

			// Add the segment with the ID
			segments.push({
				type,
				content: match[1], // The ID is in the first capture group
			});

			lastIndex = match.index + match[0].length;
		}
	};

	// Process chart tags first
	processRegex(chartTagRegex, 'chart');

	// Reset lastIndex if nothing was found
	if (segments.length === 0) {
		lastIndex = 0;
	}

	// Then process artifact tags
	processRegex(artifactTagRegex, 'artifact');

	// Reset lastIndex again if no artifacts or charts were found
	if (segments.length === 0) {
		lastIndex = 0;
	}

	// Then process proposals tags
	processRegex(proposalsTagRegex, 'proposals-group');

	// Reset lastIndex again if no proposals were found
	if (segments.length === 0) {
		lastIndex = 0;
	}

	// Then process integration credentials tags
	processRegex(integrationCredentialsTagRegex, 'integration-credentials');

	// Reset lastIndex again if no integration credentials were found
	if (segments.length === 0) {
		lastIndex = 0;
	}

	// Then process oauth required tags
	processRegex(oauthRequiredTagRegex, 'oauth-required');

	// Reset lastIndex again if no oauth required tags were found
	if (segments.length === 0) {
		lastIndex = 0;
	}

	// Then process max iterations tags
	processRegex(maxIterationsTagRegex, 'maxiterations');

	// Add remaining text after the last tag
	if (lastIndex < content.length) {
		segments.push({
			type: 'text',
			content: content.substring(lastIndex),
		});
	}

	return segments.length ? segments : [{ type: 'text', content }];
}
